import { inject, Injectable, NgZone } from '@angular/core';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';
import { SIGNALR_RECONNECT_INTERVALS_MS } from '../../shared/const/signalr.constants';
import {
  ScannerIpCompletedDto,
  ScannerIpsCompletedDto,
  ScannerScanCancelledDto,
  ScannerScanCompletedDto,
  ScannerScanResultsReconciledDto,
} from './scanner-signalr.model';

@Injectable()
export class ScannerSignalRService {
  private readonly oauthService = inject(OAuthService);
  private readonly zone = inject(NgZone);

  private connection?: HubConnection;
  private startPromise?: Promise<void>;

  private readonly activeScanIds = new Set<string>();

  private readonly ipCompletedSubject = new Subject<ScannerIpCompletedDto>();
  private readonly ipsCompletedSubject = new Subject<ScannerIpsCompletedDto>();
  private readonly resultsReconciledSubject = new Subject<ScannerScanResultsReconciledDto>();
  private readonly scanCompletedSubject = new Subject<ScannerScanCompletedDto>();
  private readonly scanCancelledSubject = new Subject<ScannerScanCancelledDto>();

  /** Batched progress (normal mode). */
  public ipsCompleted$(): Observable<ScannerIpsCompletedDto> {
    return this.ipsCompletedSubject.asObservable();
  }

  /** Single-IP progress — dormant fallback, does not fire in normal (batch) mode. */
  public ipCompleted$(): Observable<ScannerIpCompletedDto> {
    return this.ipCompletedSubject.asObservable();
  }

  public resultsReconciled$(): Observable<ScannerScanResultsReconciledDto> {
    return this.resultsReconciledSubject.asObservable();
  }

  public scanCompleted$(): Observable<ScannerScanCompletedDto> {
    return this.scanCompletedSubject.asObservable();
  }

  public scanCancelled$(): Observable<ScannerScanCancelledDto> {
    return this.scanCancelledSubject.asObservable();
  }

  public async start(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) {
      return;
    }

    if (this.startPromise) {
      return this.startPromise;
    }

    this.connection = this.buildConnection();
    this.registerHandlers(this.connection);

    this.startPromise = this.connection
      .start()
      .then(async () => {
        for (const scanId of this.activeScanIds) {
          await this.safeInvokeSubscribe(scanId);
        }
      })
      .finally(() => {
        this.startPromise = undefined;
      });

    return this.startPromise;
  }

  public async stop(): Promise<void> {
    if (!this.connection) {
      return;
    }

    try {
      await this.connection.stop();
    } finally {
      this.connection = undefined;
      this.startPromise = undefined;
    }
  }

  public async subscribeToScan(scanId: string): Promise<void> {
    this.activeScanIds.add(scanId);

    await this.start();
    await this.safeInvokeSubscribe(scanId);
  }

  public async unsubscribeFromScan(scanId: string): Promise<void> {
    this.activeScanIds.delete(scanId);

    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('Unsubscribe', scanId);
    } catch {
      // Ignore transient disconnects during modal close.
    }
  }

  private buildConnection(): HubConnection {
    const hubUrl = `${environment.apiUrls.bffAdmin}/hub/scanner-progress`;

    return new HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: true,
        withCredentials: false,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => this.oauthService.getAccessToken() ?? '',
      })
      .withAutomaticReconnect([...SIGNALR_RECONNECT_INTERVALS_MS])
      .configureLogging(environment.production ? LogLevel.Critical : LogLevel.Information)
      .build();
  }

  private registerHandlers(connection: HubConnection): void {
    connection.on('scan.ipsCompleted', (payload: ScannerIpsCompletedDto) => {
      this.zone.run(() => this.ipsCompletedSubject.next(payload));
    });

    // Dormant fallback for the single-IP path; harmless to keep registered.
    connection.on('scan.ipCompleted', (payload: ScannerIpCompletedDto) => {
      this.zone.run(() => this.ipCompletedSubject.next(payload));
    });

    connection.on('scan.resultsReconciled', (payload: ScannerScanResultsReconciledDto) => {
      this.zone.run(() => this.resultsReconciledSubject.next(payload));
    });

    connection.on('scan.completed', (payload: ScannerScanCompletedDto) => {
      this.zone.run(() => this.scanCompletedSubject.next(payload));
    });

    connection.on('scan.cancelled', (payload: ScannerScanCancelledDto) => {
      this.zone.run(() => this.scanCancelledSubject.next(payload));
    });

    connection.onreconnected(() => {
      void (async (): Promise<void> => {
        for (const scanId of this.activeScanIds) {
          await this.safeInvokeSubscribe(scanId);
        }
      })();
    });
  }

  private async safeInvokeSubscribe(scanId: string): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('Subscribe', scanId);
    } catch {
      // Automatic reconnect will retry subscriptions.
    }
  }
}
